import React from 'react';
import clsx from 'clsx';

type Props = {
    isOpen: boolean;
    statusText: string;
    darkMode?: boolean;
};

type StatusEntry = {
    id: number;
    text: string;
};

type State = {
    statusHistory: StatusEntry[];
    nextId: number;
};

/**
 * A modal overlay that displays a loading spinner with dynamic status text.
 * Shows the last 3 status messages with a scrolling animation effect.
 */
export default class LoadingModal extends React.Component<Props, State> {
    state: State = {
        statusHistory: this.props.statusText
            ? [{ id: 0, text: this.props.statusText }]
            : [],
        nextId: 1
    };

    componentDidUpdate(prevProps: Props) {
        // When status text changes, add new entry to history
        if (prevProps.statusText !== this.props.statusText && this.props.statusText) {
            this.setState(prev => ({
                statusHistory: [
                    ...prev.statusHistory.slice(-2), // Keep last 2, new one makes 3
                    { id: prev.nextId, text: this.props.statusText }
                ],
                nextId: prev.nextId + 1
            }));
        }

        // Reset history when modal opens
        if (!prevProps.isOpen && this.props.isOpen) {
            this.setState({
                statusHistory: this.props.statusText
                    ? [{ id: this.state.nextId, text: this.props.statusText }]
                    : [],
                nextId: this.state.nextId + 1
            });
        }
    }

    render() {
        const { isOpen, darkMode: _darkMode } = this.props;
        const { statusHistory } = this.state;

        if (!isOpen) return null;

        return (
            <div
                className={clsx(
                    "fixed inset-0 z-[10000] flex items-center justify-center",
                    "bg-black/40 dark:bg-black/60 backdrop-blur-sm"
                )}
            >
                <div
                    className={clsx(
                        "bg-white dark:bg-[#1c1e24] rounded-xl shadow-2xl",
                        "border border-slate-200 dark:border-white/10",
                        "px-6 py-5 max-w-sm w-full mx-4",
                        "flex flex-col items-center gap-3"
                    )}
                >
                    {/* Spinner */}
                    <div className="relative w-8 h-8">
                        <div
                            className={clsx(
                                "absolute inset-0 rounded-full border-2",
                                "border-slate-200 dark:border-white/10"
                            )}
                        />
                        <div
                            className={clsx(
                                "absolute inset-0 rounded-full border-2 border-transparent",
                                "border-t-accent-teal dark:border-t-accent",
                                "animate-spin"
                            )}
                        />
                    </div>

                    {/* Status history with scroll effect */}
                    <div className="relative w-full h-[3.5em] overflow-hidden">
                        <div className="absolute inset-0 flex flex-col justify-end items-center">
                            {statusHistory.map((entry, index) => {
                                const position = statusHistory.length - 1 - index; // 0 = newest (bottom), 1 = middle, 2 = oldest (top)
                                return (
                                    <div
                                        key={entry.id}
                                        className={clsx(
                                            "text-xs text-center font-mono w-full",
                                            "transition-all duration-300 ease-out",
                                            "leading-[1.15em]",
                                            // Opacity based on position
                                            position === 0 && "text-slate-600 dark:text-white/70",
                                            position === 1 && "text-slate-400 dark:text-white/40",
                                            position === 2 && "text-slate-300 dark:text-white/20"
                                        )}
                                        style={{
                                            animationName: 'slideUp',
                                            animationDuration: '300ms',
                                            animationTimingFunction: 'ease-out',
                                            animationFillMode: 'forwards'
                                        }}
                                    >
                                        {entry.text}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Keyframe animation for slide up */}
                <style>{`
                    @keyframes slideUp {
                        from {
                            opacity: 0;
                            transform: translateY(0.5em);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}</style>
            </div>
        );
    }
}
